<template>
  <div class="form-wrapper">
    <div v-for="(rowFields, rowIndex) in groupedFields" :key="rowIndex" :class="{'form-row': section.formType !== 'allText'}">
      <div
        v-for="field in rowFields"
        :key="field.name"
        class="form-field d-flex mt-3"
        :style="{ flex: `0 0 ${100 / rowFields?.length}%` }"
      >
        <div class="label-wrapper" v-if="section.formType !== 'allText'">
          <p class="label mr-10 pl-5" :style="{width: `${section?.width ? section.width : 200}px`}" v-highlight-required>{{ field.label }}</p>
        </div>
        <div v-else class="mobile-label mr-10">
          <p class="mr-10 pl-5" :style="{width: `${section?.width ? section.width : 200}px`}" v-highlight-required>{{ field.label }}</p>
        </div>
          <div v-if="field.type === 'text'">
            <p>{{formData[field.name]}}</p>
          </div>
          <div v-if="field.type === 'text-status'" class="d-flex">
            <div class="rounded-circle mx-auto mr-2" style="height: 20px; width: 20px;" :class="['bg-' + (field.color || 'green')]"></div>
            <p>{{formData[field.name]}}</p>
          </div>
          <div v-if="field.type === 'text-document'">
            <span class="mdi mdi-file-download mr-2"></span>
            <a :href="field.documentUrl" download>{{formData[field.name]}}</a>
          </div>
          <v-text-field
            v-if="field.type === 'text-input' && !isPdfMode"
            :placeholder="field.placeholder"
            v-model="formData[field.name]"
            variant="outlined"
            density="compact"
            @update:modelValue="value => $emit('update:data', { ...data, [field.name]: value })"
            :disabled="field.disabled || readonlyStatuses.includes($route.query.status)"
            :required="field.required"
            :ref="'field_' + field.name"
            :rules="[
              v => {
                if (!v) return !field.required || 'Поле обязательно';
                if (!field.pattern) return true;
                const regex = new RegExp(field.pattern);
                return regex.test(v) || (field.message || 'Неверный формат');
              }
            ]"
          />     
          <v-file-input
            v-if="field.type === 'file' && isShow(field) && !isPdfMode"
            variant="outlined"
            density="compact"
            :ref="'field_' + field.name"
            @update:modelValue="value => $emit('update:data', { ...data, [field.name]: value })"
            :disabled="field.disabled"
            chips
            :required="field.required"
            :multiple="field.multiple || false"
            :accept="field.accept || ''"
            :rules="[
              v => {
                const files = Array.isArray(v) ? v : v ? [v] : [];
                if (files?.length === 0)
                  return !field.required || 'Поле обязательно';

                const maxSize = field.maxSize || Infinity;

                for (const file of files) {
                  if (file.size > maxSize) {
                    return `Файл ${file.name} превышает максимальный размер ${maxSize } байт`;
                  }
                }

                return true;
              }
            ]"
          />
          <v-text-field
            v-if="field.type === 'number-input' && isShow(field) && !isPdfMode"
            :placeholder="field.placeholder"
            v-model="formData[field.name]"
            variant="outlined"
            density="compact"
            :ref="'field_' + field.name"
            @update:modelValue="value => $emit('update:data', { ...data, [field.name]: value })"
            :disabled="field.disabled || readonlyStatuses.includes($route.query.status)"
            :required="field.required"
            type="number"
            :min="field.min || 0"
            @input="onNumberInput(field, $event)"
            :rules="[
              v => {
                if (v === null || v === undefined || v === '') 
                  return !field.required || 'Поле обязательно';
                const num = Number(v);
                if (isNaN(num)) return 'Должно быть числом';
                if (field.pattern) {
                  const regex = new RegExp(field.pattern);
                  if (!regex.test(v)) return field.message || 'Неверный формат';
                }
                return true;
              }
            ]"
          />
          <v-text-field
            v-else-if="field.type === 'calculated' && !isPdfMode"
            :value="calculate(field)"
            readonly
            variant="outlined"
            density="compact"
            :required="field.required || readonlyStatuses.includes($route.query.status)"
            :disabled="field.disabled"
          />
          <v-select
            v-if="field.type === 'select' && !isPdfMode"
            :placeholder="field.placeholder"
            v-model="formData[field.name]"
            variant="outlined"
            density="compact"
            :item-title="item => item.shortForm"
            :item-value="item => item.shortForm"
            :required="field.required"
            :disabled="field.disabled || readonlyStatuses.includes($route.query.status)"
            @update:modelValue="value => handleSelectChange(value, field)"
            :items="distionaryItems[field.code]"
          >
          <template v-slot:append-item>
            <div class="select-pagination ml-5">
              <v-btn
              size="small"
              :color="distionaryItemsOffSet[field.code] === 0 ? 'primary' : ''"
              @click="updatePage(0, field.code)" 
              v-if="distionaryItemsOffSet[field.code] !== 0"
            >
              1
            </v-btn>

            <span v-if="distionaryItemsOffSet[field.code] > 2" class="px-2">…</span>

            <!-- текущая и две следующие -->
            <template v-for="i in 3" :key="'mid-'+i">
              <v-btn
                v-if="distionaryItemsOffSet[field.code] + i - 1 < distionaryItemsLength[field.code] - 1"
                size="small"
                :color="distionaryItemsOffSet[field.code] === distionaryItemsOffSet[field.code] + i - 1 ? 'primary' : ''"
                @click="updatePage(distionaryItemsOffSet[field.code] + i - 1, field.code)"
              >
                {{ distionaryItemsOffSet[field.code] + i }}
              </v-btn>
            </template>

            <span v-if="distionaryItemsOffSet[field.code] < distionaryItemsLength[field.code] - 4" class="px-2">…</span>

            <!-- последняя страница -->
            <v-btn
              size="small"
              :color="distionaryItemsOffSet[field.code] === distionaryItemsLength[field.code] - 1 ? 'primary' : ''"
              @click="updatePage(distionaryItemsLength[field.code] - 1, field.code)"
            >
              {{ distionaryItemsLength[field.code] }}
            </v-btn>
            </div>
            <!-- <v-btn
              text
              small
              @click="updatePage(distionaryItemsOffSet[field.code] - 1, field.code)"
              :disabled="distionaryItemsOffSet[field.code] <= 0"
            >
              Назад
            </v-btn> -->

            <!-- <v-btn
              text
              small
              @click="updatePage(distionaryItemsOffSet[field.code] + 1, field.code)"
              :disabled="distionaryItemsOffSet[field.code] >= distionaryItemsLength[field.code] - 1"
            >
              Вперёд
            </v-btn> -->
          </template>

          </v-select>
          <v-checkbox 
            v-if="field.type === 'checkbox'"
            v-model="formData[field.name]"
            :disabled="field.disabled || readonlyStatuses.includes($route.query.status)"
          />
          <date-picker-field v-if="field.type === 'date' && !isPdfMode" v-model="formData[field.name]"/>
          <p v-if="isPdfMode">{{ formData[field.name] }}</p>
      </div>
    </div>
    <v-alert text="Отчёт отправлен, редактирование недоступно. Для просмотра PDF нажмите кнопку 'Открыть PDF'" v-if="readonlyStatuses.includes($route.query.status)"></v-alert>
    <div class="form-actions d-flex">
      <v-col class="text-left ml-3" v-if="section.isModal">
        <v-btn color="blue-grey-lighten-4">Закрыть</v-btn>
      </v-col>
      <v-col cols="12" class="text-right ml-3">
        <v-btn color="teal-lighten-1 mr-10" :disabled="!isFormCompletelyValid" v-if="section?.button && section?.button == 'apply'">Подать заявку</v-btn>
        <div v-else>
          <v-btn color="teal-lighten-1 mr-10" :disabled="!isFormCompletelyValid" @click="saveForm(false)">Сохранить</v-btn>
          <v-btn color="teal-lighten-1 mr-10" :disabled="!isFormCompletelyValid" @click="onClickSign()"> Подписать и отправить </v-btn>
          <!-- <v-btn v-else color="teal-lighten-1 mr-10"> Открыть PDF</v-btn> -->
        </div>
        <!-- <v-btn color="teal-lighten-1 mr-10" @click="subscribeForm">Подписать</v-btn> -->
      </v-col>
      <v-snackbar v-model="snackbar" :timeout="3000" :color="color"> {{ text }} </v-snackbar>
    </div>
  </div>
     <v-dialog v-model="selectCertDialog" max-width="600px">
        <v-card>
          <v-card-title class="text-h6">
            Выберите сертификат
          </v-card-title>

          <v-card-text>
            <v-list density="compact" nav>
              <v-list-item
                v-for="cert in certList"
                :key="cert.thumbprint"
                @click="onSelectCert(cert)"
                class="cursor-pointer"
              >
                <v-list-item-title>
                  {{ cert.subject }}
                </v-list-item-title>

                <v-list-item-subtitle>
                  Действителен: {{ cert.validFrom }} — {{ cert.validTo }}
                </v-list-item-subtitle>

                <v-list-item-subtitle class="text-caption">
                  Отпечаток: {{ cert.thumbprint }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>

          <v-card-actions>
            <v-spacer/>
            <v-btn variant="text" @click="selectCertDialog = false">Закрыть</v-btn>
          </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import requestService from '@/services/request.service';
import DatePickerField from './DatePickerField.vue'
import html2pdf from 'html2pdf.js'
import plicanteRequestService from '@/services/plicante.request.service';
import { mapGetters } from 'vuex'
import { getCertificates, signFileWithCert } from '@/services/cryptoSignService'

export default {
  components: { DatePickerField, }, 
  props: {
    fields: Array,
    data: Object,
    section: Object,
    currentForm: Object,
    json: Object,
    readonlyStatuses: Array,
    distionaryItems: Object,
    metaAttributesData: Array,
    object: Object,
    distionaryItemsLength: Object,
    distionaryItemsOffSet: Object,
    userInn: Object,
    isPdfMode: Boolean,
  },
  emits: ['update:data', 'updateObject', 'updateDictionary', 'saveIntoPlicanteObject', 'ongetMetaAttributes', 'getValuesForManaged'],
  data() {
    return {
      items: ['Тракторы всех марок', 'Комбайны зерноуборочные', 'Комбайны кормоуборочные', 'Машины сельскохозяйственные', 'Машины для животноводства, птицеводства и кормопроизводства'],
      mapping: null,
      form: null,
      html: null,
      formulas: null,
      snackbar: false,
      text: null,
      color: 'green',
      meta: null,
      selectedThumbprint: null,
      tempCurrentYear: null,
      tempLastYear: null,
      certList: [],
      selectedCert: null,
      selectCertDialog: false,
    }
  },
  computed: {
    ...mapGetters(['getUserData']),
    groupedFields() {
      const visibleFields = this.fields
        .filter(field => this.isShow(field) && field.type) 
        .map((field, index) => ({ ...field, __index: index }));

      const output = [];
      let currentRow = [];
      let currentY = null;
      const usedX = new Set();

      const sortedFields = [...visibleFields].sort((a, b) => {
        const yA = typeof a.y === 'number' ? a.y : a.__index;
        const yB = typeof b.y === 'number' ? b.y : b.__index;
        if (yA !== yB) return yA - yB;

        const xA = typeof a.x === 'number' ? a.x : Infinity;
        const xB = typeof b.x === 'number' ? b.x : Infinity;
        return xA - xB;
      });

      for (const field of sortedFields) {
        const hasX = typeof field.x === 'number';
        const hasY = typeof field.y === 'number';

        if (!hasY || !hasX) {
          // если нет координат — добавляем текущую строку и вставляем поле отдельно
          if (currentRow?.length) {
            output.push([...currentRow]);
            currentRow = [];
            usedX.clear();
          }
          output.push([field]);
          currentY = null;
          continue;
        }

        if (currentY === null || currentY !== field.y) {
          if (currentRow?.length) {
            output.push([...currentRow]);
            currentRow = [];
            usedX.clear();
          }
          currentY = field.y;
        }

        if (usedX.has(field.x)) {
          output.push([...currentRow]);
          currentRow = [];
          usedX.clear();
        }

        currentRow.push(field);
        usedX.add(field.x);
      }

      if (currentRow?.length) {
        output.push([...currentRow]);
      }

      return output;
    },
    formData: {
      get() {
        return this.data
      },
    },
    allRequiredFieldsFilled() {
      return this.fields
        .filter(f =>
          ['text-input', 'date', 'select', 'checkbox', 'number-input', 'file'].includes(f.type) && f.required && !f.disabled
        )
        .every(f => {
          const value = this.formData[f.name];
          if (f.type === 'select') {
            return value !== undefined && value !== null && value !== '';
          }
          if (f.type === 'date') {
            return !!value && !isNaN(new Date(value).getTime());
          }
          return value !== undefined && value !== null && String(value).trim() !== '';
        });
    },
    isValid() {
      const pairs = {};

      for (const field of this.fields) {
        if (!field.pairId) continue;
        if (!pairs[field.pairId]) pairs[field.pairId] = [];
        pairs[field.pairId].push(field.name);
      }

      return Object.values(pairs).some(([name1, name2]) => {
        const val1 = this.formData[name1]?.toString().trim();
        const val2 = this.formData[name2]?.toString().trim();
        return val1 && val2;
      });
    },
    isFormCompletelyValid() {
      const requireModel = this.section?.requireModel || 'all'; 
      if (requireModel === 'atLeastOnePair') {
        return this.isValid && this.allRequiredFieldsFilled && this.areAllFieldsValid();
      }
      return this.allRequiredFieldsFilled && this.areAllFieldsValid();
    }
  },
  methods: {
    onSelectCert(cert) {
      this.selectedThumbprint = cert.thumbprint
      this.selectCertDialog = false
      this.subscribeForm()
    },
    async onClickSign() {

      try {
        // 1. ждём инициализации CryptoPro
        const certificates  = await getCertificates()

        if (!certificates.length) {
          alert("Нет доступных сертификатов")
          return
        }

        // 2. выбираем первый сертификат
        this.certList = certificates

        // открываем диалог с выбором сертификата
        this.selectCertDialog = true


        // 3. запускаем твой текущий функционал
        // await this.subscribeForm()

      } catch (e) {
        console.error("Ошибка подписи:", e)
        alert("Ошибка: " + e.message)
      }

    },
    updatePage(i, code) {
      this.$emit('updateDictionary', i, code)
    },
    handleSelectChange(value, field) {
      this.$emit('update:data', {
        ...this.data,
        [field.name]: value
      })

      this.onSelectChange(field)
    },
    onSelectChange(field) {
      // console.log(field)
      if (field.relatedWithCode) {
        const meta = this.metaAttributesData.find(m => m.code?.trim() === field.code?.trim())
        let currentItem = this.distionaryItems[meta.code.trim()]?.find(el => el.shortForm == this.currentForm.data[field.name]);
        currentItem.attributeValueTypes.forEach(el => {
          if (el.code == 'managed_dictionary') {
            const id = el.value.dataList[0].data.memberId
            this.$emit('getValuesForManaged', id, field.relatedWithCode)
          }
        })
      }
    },
    onNumberInput(field, event) {
      let val = event.target.value
      if (field.max !== undefined && val.length > field.max) {
        val = val.slice(0, field.max)
      }
      this.formData[field.name] = val
    },
    fillTemplate(template, values) {
      let result = template
      for (const [key, val] of Object.entries(values)) {
        const placeholder = key
        let value = val !== undefined ? val : ''
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
          const [y, m, d] = value.split('-')
          value = `${d}.${m}.${y}`
        }
        result = result.replace(placeholder, value)
      }
      return result
    },
    async formJsonToPdf() {
      let template = this.html

      const templateValues = {}

      // 1. маппинг placeholder’ов
      Object.entries(this.mapping).forEach(([field, placeholder]) => {
        if (placeholder == '$TITLE') {
          templateValues[placeholder] = this.meta.app.name
        }

        if (placeholder == '$FIO') {
          templateValues[placeholder] =
            this.meta.firstName + ' ' + this.meta.secondName + ' ' + this.meta.lastName
        }

        if (placeholder == '$POSITION') {
          templateValues[placeholder] = this.meta.position
        }

        if (this.meta[field] !== undefined) {
          templateValues[placeholder] = this.meta[field]
        }
      })

      // 2. маппинг данных
      for (const key in this.data) {
        const placeholder = this.mapping[key]
        if (placeholder) {
          const value = this.data[key]
          templateValues[placeholder] = isNaN(value) ? value : Number(value)
        }
      }

      // 3. формулы
      Object.entries(this.form.formulas).forEach(([key, formula]) => {
        if (formula.startsWith('all ')) {
          const ref = formula.replace('all ', '').trim()
          templateValues[key] = Number(templateValues[ref]) || 0
        } else {
          let expr = formula
          Object.keys(templateValues).forEach(vk => {
            expr = expr.replaceAll(vk, `(${Number(templateValues[vk]) || 0})`)
          })
          try {
            templateValues[key] = Function(`return ${expr}`)()
          } catch {
            templateValues[key] = 0
          }
        }
      })

      // 4. HTML → DOM
      const filledTemplate = this.fillTemplate(template, templateValues)
      const container = document.createElement('div')
      container.innerHTML = filledTemplate
      container.style.padding = '10px'
      container.style.marginLeft = '10mm'
      container.style.width = '277mm'
      container.style.transform = 'scale(1)'
      container.style.transformOrigin = 'top left'

      document.body.appendChild(container)

      // 5. Генерация PDF
      const opt = {
        margin: 0,
        filename: 'report.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
      }

      const pdfWorker = html2pdf().set(opt).from(container)
      const pdfBlob = await pdfWorker.outputPdf('blob');
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result.split(',')[1]) // чистая base64
        reader.onerror = reject
        reader.readAsDataURL(pdfBlob)
      })
      await pdfWorker.save()

      document.body.removeChild(container)

      // ✔ отправляем напрямую Blob
      this.saveForm(true, pdfBlob, pdfBase64)
    },
    subscribeForm() {
      if (this.currentForm['html-name'] && this.currentForm['json-name']) {
        const htmlUrl = `${process.env.VUE_APP_BASE_URL}/html-forms-storage/${this.currentForm['html-name']}`
        const jsonUrl = `${process.env.VUE_APP_BASE_URL}/json-forms-storage/${this.currentForm['json-name']}`
        requestService.get(htmlUrl)
          .then(html => {
            this.html = html.data
            requestService.get(jsonUrl)
            .then(json => {
              this.form = json.data;
              this.meta = this.getUserData;
              this.form.data = this.currentForm.data;
              this.mapping = json.data.mapping;
              this.formulas = json.data.formulas;
              this.formJsonToPdf()
            })
            .catch(err => {
              console.log(err)
            })
          })
          .catch(err => {
            console.log(err)
          })
      }
    },
    areAllFieldsValid() {
      return this.fields
        .filter(f =>
          ['text-input', 'date', 'select', 'checkbox', 'number-input', 'file'].includes(f.type) &&
          !f.disabled
        )
        .every(f => {
          const value = this.formData[f.name];

          if (f.required) {
            if (value === undefined || value === null || String(value).trim() === '') return false;
          }

          if (f.pattern) {
            const regex = new RegExp(f.pattern);
            if (!regex.test(String(value))) return false;
          }

          if (f.type === 'date') {
            if (!value || isNaN(new Date(value).getTime())) return false;
          }

          if (f.type === 'number-input') {
            const num = Number(value);
            if (isNaN(num)) return false;
            if (f.min !== undefined && num.length < f.min) return false;
            if (f.max !== undefined && num.length > f.max) return false;
          }

          if (f.type === 'file') {
            if (f.required && (!value || (Array.isArray(value) && value?.length === 0))) return false;
            if (f.maxSize && value) {
              const files = Array.isArray(value) ? value : [value];
              for (const file of files) {
                if (file.size > f.maxSize) return false;
              }
            }
          }

          return true;
        });
    },
    checkRequired(field) {
      if (field.disabled) {
        return false
      } else return field.required
    },
    calculate(field) {
      try {
        const formula = field.formula;
        const expr = formula.replace(/[a-zA-Z_]\w*/g, v => {
          const value = parseFloat(this.formData[v]);
          return isNaN(value) ? 0 : value;
        });
        const result = eval(expr);
        if (!isFinite(result)) return '—'; 

        return field.format === 'percentage'
          ? `${result.toFixed(field.precision)}%`
          : result.toFixed(field.precision);
      } catch (e) {
        return '—';
      }
    },
    isShow(field) {
      let show = true
      if (field.hiddenWith) {
        for (let i of this.fields) {
          if (field.hiddenWith.ids?.length && field.hiddenWith.ids.includes(i?.id) && field.hiddenWith.values?.length && field.hiddenWith.values.includes((this.formData[i?.name])) || (this.formData[i?.name]?.length == 0 && field.hiddenWith.ids.includes(i?.id))) {
            show = false
          }
        }
      }
      return show
    },
    saveForm(withSign = false, pdfBlob, pdfBase64) {
      const payload = this.currentForm 
      // payload.data = []
      // for (const [key, value] of Object.entries(this.currentForm.data)) {
      //   const obj = {
      //     "id": null,
      //     "field": key,
      //     "value": value
      //   }
      //   payload.data.push(obj)
      // }
      if (this.currentForm.saveJsonIntoPlicante) {
        if (!this.$route.query.dataId) {
          let selected = this.certList.find(
            c => c.thumbprint === this.selectedThumbprint
          ) 
          this.$emit('saveIntoPlicanteObject', withSign, pdfBase64, selected)
        } else {
          this.saveToPlicante(payload, withSign, pdfBlob)      
        }
      }
      if (this.currentForm.saveIntoPlicanteObject) {
        this.saveIntoPlicanteObject()
      }
    }, 
    saveIntoPlicanteObject() {
      const url = `${process.env.VUE_APP_BASE_URL}/register-rest/operator/${this.$route.query.instanceId ? 'updateObjectInstance' : 'createObjectInstance'}`
      console.log(url)
      const metaAttributesDataArr = []
      this.section.fields.forEach(field => {
        if (field.code) {
          const meta = this.metaAttributesData.find(m => m.code?.trim() === field.code?.trim())
          if ((!field.oldValue && this.currentForm.data[field.name].length == 0) || (field.oldValue == this.currentForm.data[field.name])) {
            return
          }
          let newMetaAttr = meta
          if (meta.attributeType == 'STRING' || meta.attributeType == 'DOUBLE' || meta.attributeType == 'BOOLEAN' || meta.attributeType == 'LONG' ) {
            if (!meta.valueTypeInstance?.dataListInstance[0]?.data) {
              newMetaAttr.valueTypeInstance.dataListInstance[0] = {
                data: null
              }
            }
            if (meta.attributeType == 'STRING') {
              newMetaAttr.valueTypeInstance.dataListInstance[0].data = this.currentForm.data[field.name]
              newMetaAttr.valueTypeInstance.dataListInstance[0].type = 'StringData'
            }
            
            if (meta.attributeType == 'LONG') {
              newMetaAttr.valueTypeInstance.dataListInstance[0].data = +(this.currentForm.data[field.name])
              newMetaAttr.valueTypeInstance.dataListInstance[0].type = 'LongData'
            }

            if (meta.attributeType == 'DOUBLE') {
              newMetaAttr.valueTypeInstance.dataListInstance[0].data = +(this.currentForm.data[field.name])
              newMetaAttr.valueTypeInstance.dataListInstance[0].type = 'DoubleData'
            }

            if (meta.attributeType == 'BOOLEAN') {
              newMetaAttr.valueTypeInstance.dataListInstance[0].data = Boolean(this.currentForm.data[field.name])
              newMetaAttr.valueTypeInstance.dataListInstance[0].type = 'BooleanData'
            }

            metaAttributesDataArr.push(newMetaAttr)
          } 
          if (meta.attributeType === 'RELATION_DICTIONARY' || meta.attributeType === "RELATION_REVERSE") {
            let currentItem = this.distionaryItems[meta.code.trim()]?.find(el => el.shortForm == this.currentForm.data[field.name]);
            let oldItem = field.oldValue && field.oldValue.length > 0 ? this.distionaryItems[meta.code.trim()]?.find(el => el.shortForm == field.oldValue) : null;
            if (currentItem) {

              if (oldItem && oldItem.id) {
                newMetaAttr.valueTypeInstance.dataListInstance[1] = {} 

                newMetaAttr.valueTypeInstance.dataListInstance[1] = {
                  action: "DELETE", 
                  data: oldItem.id,
                  type: "LongData"
                }
              }

              newMetaAttr.valueTypeInstance.dataListInstance[0] = {}

              newMetaAttr.valueTypeInstance.dataListInstance[0] = {
                action: "CREATE", 
                data: currentItem.id,
                type: "LongData"
              }
              metaAttributesDataArr.push(newMetaAttr)
            }
          }
          }
      })
      if (this.currentForm.innField && this.metaAttributesData) {
        this.metaAttributesData.forEach(el => {
          if (el.code == this.currentForm.innField) {
            el.valueTypeInstance.dataListInstance = [{
              "data": this.userInn.id,
              "type": "LongData",
              "action": "CREATE"
            }]
            metaAttributesDataArr.push(el)
          }
        })
      }
      const payload = {
        "objectInstanceRepresentationType": {
          "id": this.$route.query.instanceId,
          "version": this.$route.query.version,
          "versionDate": this.object.versionDate,
          "creationDate": null,
          "templateId": this.object.templateId,
          "shortForm": null,
          "accessType": "ACCESS_FULL",
          "templateType": "NONE",
          "syncAttributes": [],
          "referencingObjects": [],
          "metaAttributesData": metaAttributesDataArr,
          "templateCode": null
        },
      "getBinaries": true,
      "getLinked": false
      }
      plicanteRequestService.post(url, payload)
      .then(data => {
        this.$emit('updateObject', data.data);
        this.snackbar = true;
        this.color = 'green'
        this.text = 'Форма успешно сохранена'
        setTimeout(() => {
          if (this.object.id == 0) {
            this.$router.push(this.currentForm.to || `/table/${this.currentForm.tableId}`)
          }
        }, 500);
      })
      .catch(err => {
        this.snackbar = true;
        this.color = 'red';
        this.text = err.response.data.errorMsg
      })
    },
    sendPutSaveForm(url, payload) {
      requestService.put(url, payload)
      .then(() => {
        this.snackbar = true;
        this.saveToPlicante(payload)
        this.color = 'green'
        this.text = 'Форма успешно сохранена'
      })
      .catch(() => {
        this.snackbar = true;
        this.color = 'red'
        this.text = 'Ошибка при сохранении формы'
      })
    },
    async saveToPlicante(payload, withSign, pdfBlob) {
      let meta = null
      if (this.currentForm.pdfFileReport) {
        meta = this.metaAttributesData.find(el => el.code?.trim() === this.currentForm.pdfFileReport)
      }

      const urlUpload = `${process.env.VUE_APP_BASE_URL}/register-rest/operator/v2/binary-data/upload`
      const urlDelete = `${process.env.VUE_APP_BASE_URL}/register-rest/operator/v2/binary-data/delete`

      const instanceId = this.$route.query.instanceId
      const attributeId = this.$route.query.attributeId
      let version = this.$route.query.version
      const dataId = this.$route.query.dataId

      try {

        if (dataId) {
          const deletePayload = { dataId, version, instanceId, attributeId }

          const deleteRes = await fetch(urlDelete, {
            method: "POST",
            headers: {
              "Accept": "application/json",
              "Content-Type": "application/json",
              "Authorization": 'Basic ' + btoa(`${localStorage.getItem('token')}` + ':Bearer')
            },
            body: JSON.stringify(deletePayload)
          })

          const deleteData = await deleteRes.json()
          version = deleteData.version || version

          // Убираем dataId из query
          const newQuery = { ...this.$route.query }
          delete newQuery.dataId
          this.$router.replace({ path: this.$route.path, query: newQuery })
        }

        const formData = new FormData()
        formData.append("version", version)
        formData.append("instanceId", instanceId)
        formData.append("attributeId", attributeId)

        const file = new Blob([JSON.stringify(payload)], { type: "application/octet-stream" })
        formData.append("file", file, "Файл json отчета.json")
        formData.append("name", "file.json")
        formData.append("comment", "string")

        const uploadRes = await fetch(urlUpload, {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "Authorization": 'Basic ' + btoa(`${localStorage.getItem('token')}` + ':Bearer')
          },
          body: formData
        })

        const uploadData = await uploadRes.json()
        version = uploadData.version || version

        if (!withSign) {
          this.$router.push(this.currentForm.to || `/table/${this.currentForm.tableId}`)
          return
        }

        if (meta?.valueTypeInstance?.dataListInstance?.length) {
          for (const fileItem of meta.valueTypeInstance.dataListInstance) {

            const delPayload = {
              dataId: fileItem.id,
              version,
              instanceId,
              attributeId: meta.attributeId
            }

            const delRes = await fetch(urlDelete, {
              method: "POST",
              headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": 'Basic ' + btoa(`${localStorage.getItem('token')}` + ':Bearer')
              },
              body: JSON.stringify(delPayload)
            })

            const delData = await delRes.json()
            version = delData.version || version
          }
        }

        // 1-я загрузка
        const pdfUpload1 = await this.uploadPdf(pdfBlob, version, instanceId, meta, urlUpload)
        version = pdfUpload1.version || version

        // 2-я загрузка
        const selected = this.certList.find(
          c => c.thumbprint === this.selectedThumbprint
        )

        const { signatureBlob } = await signFileWithCert(pdfBlob, selected)

        const pdfUpload2 = await this.uploadPdf(signatureBlob.signatureBlob, version, instanceId, meta, urlUpload) // вместо 2 файла - подпись

        version = pdfUpload2.version || version

        this.$emit('ongetMetaAttributes')
        
        this.$router.push({
          path: this.$route.path,
          query: {
            ...this.$route.query,
            dataId: uploadData.dataId,
            version: version
          }
        })

      } catch (err) {
        console.error(err)
      }
    },
    async uploadPdf(pdfBlob, version, instanceId, meta, urlUpload) {
      const pdfForm = new FormData()

      pdfForm.append("version", version)
      pdfForm.append("instanceId", instanceId)
      pdfForm.append("attributeId", meta.attributeId)
      pdfForm.append("file", pdfBlob, "report.pdf")
      pdfForm.append("name", "report.pdf")
      pdfForm.append("comment", "string")

      const res = await fetch(urlUpload, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Authorization": 'Basic ' + btoa(`${localStorage.getItem('token')}` + ':Bearer')
        },
        body: pdfForm
      })

      return res.json()
    }
  },
}
</script>

<style lang="css" scoped>
  .form-wrapper {
    width: 100%;
    box-sizing: border-box;
  }

  .form-row {
    display: flex;
    margin-bottom: 16px;
  }

  .form-field {
    width: 100%;
    height: 40px;
  }

  .label {
    font-size: 14px;
  }

  .label-wrapper {
    display: flex;
    align-items: center;
  }

  .mdi-file-download {
    color: var(--app-link);
    font-size: 18px;
  }

  @media (max-width: 480px) {
    .form-row {
      flex-direction: column;
      margin-bottom: 0;
    }

    .form-field {
      min-width: 0;
      height: auto;
      flex: 0 0 auto !important;
      flex-direction: column;
      align-items: stretch;
    }

    .label-wrapper,
    .mobile-label {
      width: 100%;
      min-width: 0;
      margin-right: 0 !important;
    }

    .label-wrapper .label,
    .mobile-label p {
      width: 100% !important;
      margin-right: 0 !important;
      margin-bottom: 6px;
      padding-left: 0 !important;
      overflow-wrap: anywhere;
    }

    .form-field :deep(.v-input) {
      width: 100% !important;
      max-width: 100%;
      min-width: 0;
    }

    .form-field > div:not(.label-wrapper):not(.mobile-label) {
      max-width: 100%;
      overflow-wrap: anywhere;
    }

    .form-field a {
      overflow-wrap: anywhere;
    }

    .form-actions {
      flex-direction: column;
      gap: 8px;
      margin-top: 16px;
    }

    .form-actions .v-col {
      width: 100%;
      padding: 0;
      margin-left: 0 !important;
      text-align: center !important;
    }

    .form-actions .v-col > div {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .form-actions .v-btn {
      width: 100%;
      min-height: 44px;
      height: auto;
      margin-right: 0 !important;
      white-space: normal;
    }

    .select-pagination {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      margin-left: 8px !important;
      margin-right: 8px;
    }

    .select-pagination :deep(.v-btn) {
      min-width: 32px;
      padding-inline: 6px;
    }
  }
</style>
